import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, firstValueFrom, map, Observable, of, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AddTeamMemberRequest } from 'edu-application/dist/team/base/add-team-member.request';
import { AddTeamMemberViewmodel } from 'edu-application/dist/team/base/add-team-member.viewmodel';
import { SubjectViewmodel } from 'edu-application/dist/classes/list-subjects/list-subjects.viewmodel';
import { OrgCourseViewmodel } from 'edu-application/dist/classes/get-org-courses/get-org-courses.viewmodel';
import { GetTeamMemberViewItem } from 'edu-application/dist/team/base/get-team-member.viewmodel';
import { GetGroupViewItem } from 'edu-application/dist/groups/get-groups/get-group.viewmodel';
import { BaseOrganizationViewmodel } from 'edu-application/dist/organization/base/base.organization.viewmodel';
import { OrganizationService } from '../shared/organization.service';
import { LoginService } from '../login/login.service';
import { GroupsService } from '../shared/groups.service';

export type OnboardingStepName = 'inviteTeam' | 'subjects' | 'groups';
export type OnboardingStepStatus = 'pending' | 'done' | 'skipped';

export interface OnboardingStatus {
    completed: boolean;
    dismissed: boolean;
    steps: Record<OnboardingStepName, OnboardingStepStatus>;
}

@Injectable({ providedIn: 'root' })
export class TeacherOnboardingService {
    /**
     * Live onboarding status, kept independently of the userInfo cookie. It is seeded
     * from the cookie (first paint) and refreshed via its own endpoint so we never have
     * to mutate/clear the shared userInfo cookie to keep progress fresh.
     */
    private readonly _status$ = new BehaviorSubject<OnboardingStatus | null>(null);
    readonly status$: Observable<OnboardingStatus | null> = this._status$.asObservable();

    constructor(
        private http: HttpClient,
        private organizationService: OrganizationService,
        private loginService: LoginService,
        private groupsService: GroupsService
    ) {}

    private get orgId(): string {
        return this.organizationService.getOrganizationId();
    }

    get status(): OnboardingStatus | null {
        return this._status$.value;
    }

    /** First piece of information: take whatever shipped in the userInfo cookie at login. */
    seedFromUserInfo(): OnboardingStatus | null {
        if (this._status$.value) return this._status$.value;
        let info: OnboardingStatus | undefined;
        try {
            const onboarding = this.loginService.userInfo?.organizationOnboarding;
            if (onboarding) {
                info = {
                    completed: onboarding.completed,
                    dismissed: onboarding.dismissed,
                    steps: { ...onboarding.steps },
                };
            }
        } catch {
            info = undefined;
        }
        if (info) this._status$.next(info);
        return this._status$.value;
    }

    /** Authoritative refresh via a dedicated endpoint — does NOT touch the userInfo cookie. */
    async refreshStatus(): Promise<OnboardingStatus | null> {
        try {
            const status = await firstValueFrom(
                this.http.get<OnboardingStatus>(
                    `${environment.backend_url}/organizations/onboarding?organizationId=${this.orgId}`,
                ),
            );
            this._status$.next(status);
            return status;
        } catch {
            return this._status$.value;
        }
    }

    // Observable variant so callers can cancel the in-flight request (e.g. via takeUntilDestroyed)
    // instead of letting it complete after the component that requested it is gone.
    refreshStatus$(): Observable<OnboardingStatus | null> {
        return this.http.get<OnboardingStatus>(
            `${environment.backend_url}/organizations/onboarding?organizationId=${this.orgId}`,
        ).pipe(
            tap((status) => this._status$.next(status)),
            catchError(() => of(this._status$.value)),
        );
    }

    /**
     * `organizationOnboarding` is only sent to users holding onboarding.manage, so its
     * presence is the permission check.
     */
    canOnboard(): boolean {
        try {
            const info = this.loginService.userInfo;
            return info?.type === 'team' && !!info.organizationOnboarding;
        } catch {
            return false;
        }
    }

    /**
     * Whether the setup checklist currently owns the corner of the screen. The
     * help FAB shares that corner and stands down while setup is unfinished.
     */
    isChecklistVisible(): boolean {
        if (!this.canOnboard()) return false;
        const status = this._status$.value;
        return !!status && !status.completed;
    }

    /** Locally reflect a step change without a round-trip (optimistic). */
    setStepLocally(step: OnboardingStepName, status: OnboardingStepStatus): void {
        const current = this._status$.value;
        if (!current) return;
        this._status$.next({ ...current, steps: { ...current.steps, [step]: status } });
    }

    // ── Onboarding progress ──────────────────────────────────────────
    updateStep(step: OnboardingStepName, status: OnboardingStepStatus): Promise<void> {
        return firstValueFrom(
            this.http.patch<void>(`${environment.backend_url}/organizations/onboarding`, {
                organizationId: this.orgId,
                step,
                status,
            }),
        );
    }

    dismiss(): Promise<void> {
        return firstValueFrom(
            this.http.patch<void>(`${environment.backend_url}/organizations/onboarding`, {
                organizationId: this.orgId,
                dismiss: true,
            }),
        );
    }

    // ── Team ─────────────────────────────────────────────────────────
    getTeamMembers(): Promise<GetTeamMemberViewItem[]> {
        return firstValueFrom(
            this.http.get<GetTeamMemberViewItem[]>(`${environment.backend_url}/team-members/${this.orgId}`),
        );
    }

    // Observable variant so callers can cancel the in-flight request (e.g. via takeUntilDestroyed)
    // instead of letting it complete after the component that requested it is gone.
    getTeamMembers$(): Observable<GetTeamMemberViewItem[]> {
        return this.http.get<GetTeamMemberViewItem[]>(`${environment.backend_url}/team-members/${this.orgId}`);
    }

    addTeamMember(request: Omit<AddTeamMemberRequest, 'organizationId'>): Promise<AddTeamMemberViewmodel> {
        return firstValueFrom(
            this.http.post<AddTeamMemberViewmodel>(`${environment.backend_url}/team-members`, {
                ...request,
                organizationId: this.orgId,
            }),
        );
    }

    // ── Subjects ─────────────────────────────────────────────────────
    /** Full catalog of subjects the teacher can choose from. */
    listSubjects(): Promise<SubjectViewmodel[]> {
        return firstValueFrom(
            this.http.get<SubjectViewmodel[]>(`${environment.backend_url}/tutoring-classes/subjects`),
        );
    }

    // Observable variant so callers can cancel the in-flight request (e.g. via takeUntilDestroyed)
    // instead of letting it complete after the component that requested it is gone.
    listSubjects$(): Observable<SubjectViewmodel[]> {
        return this.http.get<SubjectViewmodel[]>(`${environment.backend_url}/tutoring-classes/subjects`);
    }

    /** Subjects this organization teaches (general, session-independent). */
    getOrgSubjects(): Promise<SubjectViewmodel[]> {
        return firstValueFrom(
            this.http.get<SubjectViewmodel[]>(
                `${environment.backend_url}/organizations/subjects?organizationId=${this.orgId}`,
            ),
        );
    }

    // Observable variant so callers can cancel the in-flight request (e.g. via takeUntilDestroyed)
    // instead of letting it complete after the component that requested it is gone.
    getOrgSubjects$(): Observable<SubjectViewmodel[]> {
        return this.http.get<SubjectViewmodel[]>(
            `${environment.backend_url}/organizations/subjects?organizationId=${this.orgId}`,
        );
    }

    addOrgSubject(subjectId: string): Promise<SubjectViewmodel> {
        return firstValueFrom(
            this.http.post<SubjectViewmodel>(`${environment.backend_url}/organizations/subjects`, {
                organizationId: this.orgId,
                subjectId,
            }),
        );
    }

    removeOrgSubject(subjectId: string): Promise<void> {
        return firstValueFrom(
            this.http.delete<void>(
                `${environment.backend_url}/organizations/subjects?organizationId=${this.orgId}&subjectId=${subjectId}`,
            ),
        );
    }

    // ── Groups ───────────────────────────────────────────────────────
    getGroups(): Promise<GetGroupViewItem[]> {
        return this.groupsService.getGroups();
    }

    // Observable variant so callers can cancel the in-flight request (e.g. via takeUntilDestroyed)
    // instead of letting it complete after the component that requested it is gone.
    getGroups$(): Observable<GetGroupViewItem[]> {
        return this.groupsService.getGroups$();
    }

    /** The org's offerings (TutorCourses) for the current session — a group attaches to one. */
    getOrgCourses(): Promise<OrgCourseViewmodel[]> {
        return firstValueFrom(
            this.http.get<OrgCourseViewmodel[]>(
                `${environment.backend_url}/tutoring-classes/org?organizationId=${this.orgId}&assignedOnly=true`,
            ),
        );
    }

    // Observable variant so callers can cancel the in-flight request (e.g. via takeUntilDestroyed)
    // instead of letting it complete after the component that requested it is gone.
    getOrgCourses$(): Observable<OrgCourseViewmodel[]> {
        return this.http.get<OrgCourseViewmodel[]>(
            `${environment.backend_url}/tutoring-classes/org?organizationId=${this.orgId}&assignedOnly=true`,
        );
    }

    async addGroup(request: { name: string; groupInitials: string; courseId: string }): Promise<GetGroupViewItem> {
        const group = await firstValueFrom(
            this.http.post<GetGroupViewItem>(`${environment.backend_url}/groups`, {
                ...request,
                shortName: '',
                organizationId: this.orgId,
            }),
        );
        GroupsService.clearCache();
        return group;
    }

    // ── Organization (name + logo for the welcome hero) ──────────────
    async getOrganization(): Promise<BaseOrganizationViewmodel | undefined> {
        const userName = this.loginService.userInfo.userName;
        const orgs = await firstValueFrom(
            this.http.get<BaseOrganizationViewmodel[]>(
                `${environment.backend_url}/users/organizations?userName=${userName}`,
            ),
        );
        return orgs?.find((o) => o.id === this.orgId) ?? orgs?.[0];
    }

    // Observable variant so callers can cancel the in-flight request (e.g. via takeUntilDestroyed)
    // instead of letting it complete after the component that requested it is gone.
    getOrganization$(): Observable<BaseOrganizationViewmodel | undefined> {
        const userName = this.loginService.userInfo.userName;
        return this.http.get<BaseOrganizationViewmodel[]>(
            `${environment.backend_url}/users/organizations?userName=${userName}`,
        ).pipe(map((orgs) => orgs?.find((o) => o.id === this.orgId) ?? orgs?.[0]));
    }
}
